import { ProductPrice, ProductUnitOfMeasure, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getBooleanProperty, updateOrAddProperty } from './extension-properties-helpers';
import { ICoreContext } from '@msdyn365-commerce/core';
import { MTT_IS_ALLOWED_UOM_PROPERTY } from '../constants';

const roundPriceTo2Decimals = (val?: number): number | undefined =>
    val === undefined ? undefined : Math.round((val + Number.EPSILON) * 100) / 100;

export const isPriceDifferentFromBase = (defaultPriceRow?: ProductPrice): boolean => {
    if (!defaultPriceRow) {
        return false;
    }

    const basePrice = roundPriceTo2Decimals(defaultPriceRow.BasePrice);
    if (basePrice === undefined) {
        return false;
    }

    const compare =
        defaultPriceRow.TradeAgreementPrice !== undefined
            ? roundPriceTo2Decimals(defaultPriceRow.TradeAgreementPrice)
            : roundPriceTo2Decimals(defaultPriceRow.CustomerContextualPrice);

    return compare !== undefined && compare !== basePrice;
};

export const isDiscountedPrice = (productPrice: ProductPrice): boolean => {
    const price = productPrice?.CustomerContextualPrice ?? productPrice?.BasePrice ?? 0;
    const basePrice = productPrice
        ? Math.max(productPrice.BasePrice ?? 0, productPrice.TradeAgreementPrice ?? 0, productPrice.AdjustedPrice ?? 0)
        : 0;
    const isDiscountPrice = basePrice > price;
    return isDiscountPrice;
};

export const getOriginalPrice = (price: ProductPrice): number => {
    return price.AdjustedPrice ?? price.TradeAgreementPrice ?? price.BasePrice ?? 0;
};

export const getBasePrice = (price: ProductPrice): number => {
    const basePrice = price ? Math.max(price.BasePrice ?? 0, price.TradeAgreementPrice ?? 0, price.AdjustedPrice ?? 0) : 0;
    return basePrice;
};

export const getActivePrice = (price: ProductPrice): number => {
    if (price.CustomerContextualPrice !== undefined) {
        return price.CustomerContextualPrice;
    }

    return getOriginalPrice(price);
};

export const setIsDisabledFlag = (uom: ProductUnitOfMeasure, shouldDisable: boolean): void => {
    if (uom) {
        if (!uom?.ExtensionProperties?.length) {
            uom.ExtensionProperties = [];
        }
        updateOrAddProperty('Disabled', { BooleanValue: shouldDisable }, uom.ExtensionProperties);
    }
};

export const isUomDisabled = (uom: ProductUnitOfMeasure): boolean => {
    return getBooleanProperty(uom?.ExtensionProperties, 'Disabled');
};

export const isUomAllowed = (uom: ProductUnitOfMeasure): boolean => {
    return getBooleanProperty(uom?.ExtensionProperties, MTT_IS_ALLOWED_UOM_PROPERTY) ?? true;
};

const normalizeUomSymbol = (value?: string): string | undefined => {
    const normalizedValue = value?.trim()?.toUpperCase();
    return normalizedValue ? normalizedValue : undefined;
};

export const isDefaultUomSelected = (productDefaultUnitOfMeasure?: string, selectedUnitOfMeasure?: ProductUnitOfMeasure): boolean => {
    const defaultUomSymbol = normalizeUomSymbol(productDefaultUnitOfMeasure);
    if (!defaultUomSymbol) {
        return true;
    }

    const selectedUomSymbol = normalizeUomSymbol(selectedUnitOfMeasure?.Symbol);
    if (!selectedUomSymbol) {
        return true;
    }

    return selectedUomSymbol === defaultUomSymbol;
};

export const isDisableOrderLimitsValidationForNotDefaultUomEnabled = (config?: {
    disableOrderLimitsValidationForNotDefaultUom?: boolean;
}): boolean => config?.disableOrderLimitsValidationForNotDefaultUom === true;

export const hasUomSelectionChanged = (previousUnitOfMeasure?: ProductUnitOfMeasure, newUnitOfMeasure?: ProductUnitOfMeasure): boolean =>
    normalizeUomSymbol(previousUnitOfMeasure?.Symbol) !== normalizeUomSymbol(newUnitOfMeasure?.Symbol);

export const getQuantityOnUomSelection = (
    currentQuantity: number,
    productDefaultUnitOfMeasure: string | undefined,
    selectedUnitOfMeasure: ProductUnitOfMeasure | undefined,
    disableOrderLimitsForNotDefaultUom: boolean,
    minQuantity = 1
): number => {
    if (
        !shouldApplyOrderQuantityLimitsForSelectedUom(
            productDefaultUnitOfMeasure,
            selectedUnitOfMeasure,
            disableOrderLimitsForNotDefaultUom
        )
    ) {
        return 1;
    }

    return Math.max(currentQuantity, minQuantity);
};

export const shouldApplyOrderQuantityLimitsForSelectedUom = (
    productDefaultUnitOfMeasure: string | undefined,
    selectedUnitOfMeasure: ProductUnitOfMeasure | undefined,
    disableOrderLimitsForNotDefaultUom: boolean
): boolean => {
    if (!disableOrderLimitsForNotDefaultUom) {
        return true;
    }

    return isDefaultUomSelected(productDefaultUnitOfMeasure, selectedUnitOfMeasure);
};

// Fallback min order qty from product.Behavior when buybox state.min is not set yet (e.g. right after page refresh)
export const getMinOrderQuantityFromProduct = (minimumQuantity?: number): number | undefined => {
    if (minimumQuantity !== undefined && minimumQuantity > 0) {
        return minimumQuantity;
    }

    return undefined;
};

// Clamps quantity to min order qty when order limits apply for the selected UOM
export const applyMinOrderQuantityForDefaultUom = (
    quantity: number,
    productDefaultUnitOfMeasure: string | undefined,
    selectedUnitOfMeasure: ProductUnitOfMeasure | undefined,
    minQuantity: number | undefined,
    disableOrderLimitsForNotDefaultUom: boolean
): number => {
    if (
        !shouldApplyOrderQuantityLimitsForSelectedUom(
            productDefaultUnitOfMeasure,
            selectedUnitOfMeasure,
            disableOrderLimitsForNotDefaultUom
        )
    ) {
        return quantity;
    }

    if (minQuantity !== undefined && minQuantity > 1) {
        return Math.max(quantity, minQuantity);
    }

    return quantity;
};

export const filterUomsBasedOnZone = (
    product: SimpleProduct,
    uoms: ProductUnitOfMeasure[]
): { uomsToProcess: ProductUnitOfMeasure[]; shouldDisableDefaultUom: boolean } => {
    const defaultUomSymbol = product?.DefaultUnitOfMeasure?.toUpperCase();

    const defaultUom = uoms.find(x => x.Symbol?.toUpperCase() === defaultUomSymbol);

    const uomsToProcess = uoms.filter(uom => uom?.Symbol?.toUpperCase() === defaultUomSymbol || isUomAllowed(uom));

    const shouldDisableDefaultUom = !isUomAllowed(defaultUom!);

    if (shouldDisableDefaultUom) {
        setIsDisabledFlag(defaultUom!, true);
    }

    return { uomsToProcess, shouldDisableDefaultUom };
};

export const filterUomsBasedOnPriceDiscount = (
    product: SimpleProduct,
    uoms: ProductUnitOfMeasure[],
    prices: ProductPrice[],
    context: ICoreContext
): { uomsToProcess: ProductUnitOfMeasure[]; shouldDisableDefaultUom: boolean } => {
    const pricesDifferentFromBase = prices.filter(productPrice => isPriceDifferentFromBase(productPrice));

    const defaultUomSymbol = product?.DefaultUnitOfMeasure?.toUpperCase();

    const enableMinimumOrderQty = context?.actionContext?.requestContext?.app?.config?.enableMinimumOrderQtyForUom === true;

    const defaultUom = uoms.find(x => x.Symbol?.toUpperCase() === defaultUomSymbol);

    const uomsToProcess = uoms.filter(uom => {
        return (
            uom?.Symbol?.toUpperCase() === defaultUomSymbol ||
            pricesDifferentFromBase?.some(x => x?.UnitOfMeasure?.toUpperCase() === uom?.Symbol?.toUpperCase())
        );
    });

    let shouldDisableDefaultUom = false;

    if (enableMinimumOrderQty && defaultUom) {
        const defaultUomPrice = prices?.find(p => p?.UnitOfMeasure?.toUpperCase() === defaultUomSymbol);
        shouldDisableDefaultUom = !isPriceDifferentFromBase(defaultUomPrice) && uomsToProcess.length > 1;
        setIsDisabledFlag(defaultUom, shouldDisableDefaultUom);
    }

    return { uomsToProcess, shouldDisableDefaultUom };
};
